import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Download, ShieldCheck } from 'lucide-react';
import { copySecretText } from '../utils/clipboardSecret';
import { isTauri } from '../utils/tauri';
import { pickConfirmPositions, sameWord } from './vaultPassphraseUtils';
import './Vault.css';

/**
 * "Save your recovery phrase" — CONTRACT §6.2 step 2, reused verbatim by the
 * change screen when the user also rotates the phrase (§6.4).
 *
 * Three properties here are the design, not styling:
 *
 * 1. **The primary button is disabled until two randomly-chosen word positions
 *    are typed back.** Everything in the vault — every password, every SFTP
 *    key, every passkey — is unreachable forever if both secrets are lost, and
 *    this is the only moment the second one is on screen. A "Done" that can be
 *    clicked through is a data-loss generator.
 * 2. **"Words can repeat. That is normal."** must render. Rust samples the 12
 *    words WITH replacement (`vault_key::generate_recovery_words`), so repeats
 *    are legitimate output. Without the sentence a user reads a repeat as a
 *    bug — and an implementer eventually "fixes" it with a distinctness check
 *    that silently cuts the entropy. Nothing here validates distinctness, and
 *    nothing validates a checksum: there is no checksum word.
 * 3. **Copy goes through `copySecretText`**, which clears the clipboard after
 *    60 seconds, and the 60 seconds are stated on screen. A silent wipe
 *    mid-paste is worse than a known one.
 *
 * The words live in this component's props for the life of the screen and are
 * never persisted, logged or echoed into an error string.
 */
/**
 * Accent-colour the `#3` / `#7` in "type word #3 and word #7".
 *
 * Those two numbers are the only part of the sentence the reader has to act on,
 * and they were set in the same weight and colour as the surrounding prose — so
 * finding them meant reading the whole line. This is a screen people hit once,
 * under mild stress, with twelve words they have just been told to write down.
 *
 * Splits the RENDERED string rather than composing the sentence from fragments:
 * word order differs by language, and a German or French translation must be
 * free to put the numbers wherever its grammar wants them. Any `#<digits>` gets
 * highlighted, wherever it lands. A translation that drops the `#` simply gets
 * no highlight instead of a broken sentence.
 */
export function highlightWordNumbers(text) {
  if (typeof text !== 'string') return text;
  return text.split(/(#\d+)/g).map((part, i) =>
    /^#\d+$/.test(part) ? (
      // eslint-disable-next-line react/no-array-index-key
      <strong key={i} className="vault-pass-wordnum">
        {part}
      </strong>
    ) : (
      part
    )
  );
}

export default function VaultRecoveryPhraseStep({
  words,
  busy = false,
  error = '',
  primaryLabel,
  busyLabel,
  onDone,
  /** Test seam: fixed [a, b] positions instead of two random ones. */
  positions: fixedPositions = null,
  /** Rendered above the words — used by the change flow to say what happened. */
  headline = null,
  footer = null,
}) {
  const { t } = useTranslation();
  const list = Array.isArray(words) ? words : [];

  // Chosen once per mount. Re-rolling them on every keystroke would let a user
  // who typed one word right cycle the other until it matched something they
  // remembered, which is not the check we want.
  const [a, b] = useMemo(
    () => fixedPositions || pickConfirmPositions(list.length || 12),
    [fixedPositions, list.length]
  );

  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [touched, setTouched] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const firstOk = sameWord(first, list[a - 1]);
  const secondOk = sameWord(second, list[b - 1]);
  const bothFilled = first.trim() !== '' && second.trim() !== '';
  const confirmed = firstOk && secondOk;
  // Only after a blur, and only once both are filled: telling someone their
  // word is wrong while they are still typing it is noise.
  const showMismatch = touched && bothFilled && !confirmed;

  // The 2-second "Copied!" reset must not fire into an unmounted screen — this
  // component is torn down by the commit that follows it.
  const copyTimer = useRef(null);
  const saveTimer = useRef(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    []
  );

  const handleCopy = async () => {
    const ok = await copySecretText(list.join(' '));
    if (!ok) return;
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  };

  /**
   * Save the phrase to a file the user chooses.
   *
   * Numbered one word per line, not the bare space-joined string: this file is
   * read by a human months later, possibly on paper, and the numbering is what
   * makes a transcription error findable. The header says what it is, because a
   * file called `apiboo-recovery-phrase.txt` found in Downloads with twelve
   * unexplained words in it is indistinguishable from junk.
   *
   * The warning is IN the file rather than only on screen — the screen is gone
   * by the time this matters.
   *
   * Desktop takes the native save dialog so the user picks the location and
   * nothing lands in Downloads by surprise; the browser build falls back to an
   * anchor download, which is the only option there.
   */
  const handleSave = async () => {
    if (list.length === 0) return;
    const body = [
      t('vault.recoveryFileTitle', 'Apiboo vault recovery phrase'),
      t('vault.recoveryFileCreated', 'Created {{date}}', {
        date: new Date().toISOString().slice(0, 10),
      }),
      '',
      ...list.map((w, i) => `${String(i + 1).padStart(2, ' ')}. ${w}`),
      '',
      t(
        'vault.recoveryFileWarning',
        'Anyone with these 12 words can open your vault. This file is not encrypted. Write the words down and delete it, or keep it offline.'
      ),
      '',
    ].join('\r\n');

    try {
      if (isTauri()) {
        const { save } = await import('@tauri-apps/plugin-dialog');
        const { writeTextFile } = await import('@tauri-apps/plugin-fs');
        const path = await save({
          defaultPath: 'apiboo-recovery-phrase.txt',
          title: t('vault.recoverySaveTitle', 'Save recovery phrase'),
          filters: [{ name: 'Text', extensions: ['txt'] }],
        });
        if (path == null) return; // cancelled
        await writeTextFile(path, body);
      } else {
        const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'apiboo-recovery-phrase.txt';
        a.click();
        URL.revokeObjectURL(url);
      }
      setSaved(true);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      // Never surfaces the phrase in an error string. A failed save leaves the
      // words on screen, which is the state the user was already in.
      setSaveError(true);
    }
  };

  return (
    <div className="vault-pass-body">
      <div className="vault-share-success">
        <ShieldCheck size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
        {t('vault.recoveryProtected', 'Vault protected')}
      </div>

      {headline}

      <p className="vault-pass-text">
        {t(
          'vault.recoveryIntro',
          'These 12 words are the only other way into your vault if you forget your passphrase. They are shown once, here. They are not stored on this device and not stored by Apiboo.'
        )}
      </p>
      <p className="vault-pass-text">
        {t(
          'vault.recoveryWriteItDown',
          'Write them on paper and keep them where you would keep a passport.'
        )}{' '}
        <strong>
          {t(
            'vault.recoveryNotInVault',
            'Do not save them in this vault — you need them to open it.'
          )}
        </strong>
      </p>

      <div className="totp-backup-codes">
        {list.map((w, i) => (
          // Index-keyed on purpose: words repeat, so the word itself is not a
          // stable key, and the list never reorders.
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${i}-${w}`} className="totp-backup-code-item">
            <span className="vault-pass-word-n">{i + 1}.</span> {w}
          </div>
        ))}
      </div>

      {/* Non-negotiable: Rust samples with replacement. */}
      <p className="vault-pass-hint">
        {t('vault.recoveryRepeatsOk', 'Words can repeat. That is normal.')}
      </p>

      <div className="vault-pass-row">
        <button type="button" className="vault-pass-secondary-btn" onClick={handleCopy}>
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? t('vault.recoveryCopied', 'Copied!') : t('vault.recoveryCopy', 'Copy phrase')}
        </button>
        <button type="button" className="vault-pass-secondary-btn" onClick={handleSave}>
          {saved ? <Check size={13} /> : <Download size={13} />}
          {saved ? t('vault.recoverySaved', 'Saved!') : t('vault.recoverySave', 'Save to file')}
        </button>
      </div>
      {copied && (
        <p className="vault-pass-hint">
          {t(
            'vault.recoveryClipboardClears',
            'The clipboard is cleared automatically in 60 seconds. Other apps and websites can read it until then.'
          )}
        </p>
      )}
      {saved && (
        <p className="vault-pass-hint">
          {t(
            'vault.recoveryFileWarningUi',
            'That file is not encrypted. Anyone who can read it can open your vault — move it offline or delete it once the words are on paper.'
          )}
        </p>
      )}
      {saveError && (
        <p className="vault-pass-hint">
          {t('vault.recoverySaveFailed', 'Could not save the file. The words above are still valid.')}
        </p>
      )}

      <div className="vault-pass-confirm">
        <span className="vault-pass-label">
          {highlightWordNumbers(
            t('vault.recoveryConfirmPrompt', 'Confirm you saved it — type word #{{a}} and word #{{b}}:', {
              a,
              b,
            })
          )}
        </span>
        <div className="vault-pass-confirm-row">
          <input
            type="text"
            className="vault-pass-recovery-word"
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={String(a)}
            aria-label={`Word ${a}`}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            disabled={busy}
          />
          <input
            type="text"
            className="vault-pass-recovery-word"
            value={second}
            onChange={(e) => setSecond(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder={String(b)}
            aria-label={`Word ${b}`}
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="off"
            disabled={busy}
          />
        </div>
        {showMismatch && (
          <span className="vault-modal-error">
            {t('vault.recoveryConfirmMismatch', 'That does not match the list above.')}
          </span>
        )}
      </div>

      {error && <div className="vault-modal-error">{error}</div>}

      {footer}

      <div className="modal-footer-row">
        <button
          type="button"
          disabled={!confirmed || busy}
          onClick={() => onDone && onDone()}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
        >
          {busy ? busyLabel || t('vault.recoverySaving', 'Saving…') : primaryLabel || t('vault.recoveryDone', 'Done')}
        </button>
      </div>
    </div>
  );
}
